// Account/AdminBlock.tsx

import React, { useEffect, useState } from "react";
import { VStack, Divider, Button, Text } from "native-base";
import { useAPI } from "../../../../../Universal/API/API";
import { useAppContext } from "../../../../../Universal/API/Context-API/AppContext";

import AdminCreateUser from "./AdminCreateUser";
import AdminUsersList from "./AdminUsersList";
import AdminEditUserModal from "./AdminEditUserModal";
import AdminDeleteUserModal from "./AdminDeleteUserModal"; // 👈 добавили
import { DeanteUser, EditUserPayload } from "./interfaces";

const AdminBlock: React.FC = () => {
  const { sendFormDataPostRequest, getAllData, getUtils } = useAPI();
  const { USER, setOTHER_INFO_FIELDS } = useAppContext();

  const [users, setUsers] = useState<DeanteUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);

  // редактирование
  const [editingUser, setEditingUser] = useState<DeanteUser | null>(null);

  // удаление
  const [userToDelete, setUserToDelete] = useState<DeanteUser | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    setTimeout(async () => {
      if (ensureAdmin()) {
        console.log("getUtils");
        await getUtils();
      } else {
        setOTHER_INFO_FIELDS(null);
      }
    });
  }, []);

  const ensureAdmin = (): boolean => {
    if (!USER || USER.role !== "admin") {
      setUsersError("Только администратор может работать с пользователями");
      return false;
    }
    return true;
  };

  const handleReloadUsers = async () => {
    setUsersError(null);

    if (!ensureAdmin()) return;

    setUsersLoading(true);
    try {
      const formData = new FormData();
      formData.append("access_token", USER!.access_token);

      const res = await sendFormDataPostRequest({
        link: "list-users",
        formData,
      });

      if (!res) {
        setUsersError("Ошибка сервера при загрузке пользователей");
      } else if (!res.success) {
        setUsersError(res.message ?? "Не удалось загрузить пользователей");
      } else {
        const list: DeanteUser[] = (res.users ?? []).map((u: any) => ({
          login: u.login,
          role: u.role,
          discount: Number(u.discount ?? 0),
          other_info: u.other_info ?? null,
          last_login: u.last_login ?? null,
          date_created: u.date_created ?? null,
        }));
        setUsers(list);
      }
    } catch (e: any) {
      console.error("error at list-users", e);
      setUsersError("Проблема с сетью при загрузке пользователей");
    } finally {
      setUsersLoading(false);
    }
  };

  const handleUserCreated = (user: DeanteUser) => {
    // оптимистично добавляем юзера в список
    setUsers((prev) => [...prev, user]);
  };

  const handleEditClick = (user: DeanteUser) => {
    setEditingUser(user);
  };

  const handleSaveEditedUser = async (payload: EditUserPayload) => {
    if (!ensureAdmin()) return;

    const formData = new FormData();
    formData.append("access_token", USER!.access_token);
    formData.append("target_login", payload.target_login);

    if (payload.login) formData.append("login", payload.login);
    if (payload.password) formData.append("password", payload.password);
    if (payload.role) formData.append("role", payload.role);
    if (typeof payload.discount === "number") {
      formData.append("discount", String(payload.discount));
    }
    if (payload.other_info) {
      formData.append("other_info", JSON.stringify(payload.other_info));
    }

    const res = await sendFormDataPostRequest({
      link: "update-user",
      formData,
    });

    if (!res || !res.success || !res.user) {
      throw new Error(res?.message ?? "Не удалось обновить пользователя");
    }

    setTimeout(async () => {
      await getAllData({ mode: "allData" });
    });

    const updated: DeanteUser = {
      login: res.user.login,
      role: res.user.role,
      discount: Number(res.user.discount ?? 0),
      other_info: res.user.other_info ?? null,
      last_login: res.user.last_login ?? null,
      date_created: res.user.date_created ?? null,
    };

    setUsers((prev) =>
      prev.map((u) => (u.login === payload.target_login ? updated : u))
    );
  };

  const handleDeleteClick = (user: DeanteUser) => {
    setUserToDelete(user);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    if (!ensureAdmin()) return;

    setDeleteLoading(true);
    try {
      const formData = new FormData();
      formData.append("access_token", USER!.access_token);
      formData.append("target_login", userToDelete.login);

      const res = await sendFormDataPostRequest({
        link: "delete-user",
        formData,
      });

      if (!res || !res.success) {
        throw new Error(res?.message ?? "Не удалось удалить пользователя");
      }

      setTimeout(async () => {
        await getAllData({ mode: "allData" });
      });

      setUsers((prev) =>
        prev.filter((u) => u.login !== userToDelete.login)
      );
      setUserToDelete(null);
    } catch (e: any) {
      console.error("error at delete-user", e);
      setUsersError("Не удалось удалить пользователя");
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <VStack space={4} mt={4}>
      <AdminCreateUser onUserCreated={handleUserCreated} />

      <Divider my={4} />

      <AdminUsersList
        users={users}
        loading={usersLoading}
        error={usersError}
        onReload={handleReloadUsers}
        onEditUser={handleEditClick}
        onDeleteUser={handleDeleteClick}
      />

      {/* Модалка редактирования */}
      <AdminEditUserModal
        isOpen={!!editingUser}
        user={editingUser}
        onClose={() => setEditingUser(null)}
        onSave={handleSaveEditedUser}
      />

      {/* Модалка удаления через Portal (как редактирование) */}
      <AdminDeleteUserModal
        isOpen={!!userToDelete}
        user={userToDelete}
        loading={deleteLoading}
        onCancel={() => {
          if (!deleteLoading) setUserToDelete(null);
        }}
        onConfirm={confirmDeleteUser}
      />
    </VStack>
  );
};

export default AdminBlock;
